/**
 * Circle to ask, the answer: a card drawn piece by piece on dotted paper. A marker pen moves to each part as it
 * appears; strokes draw on, boxes pop in, bars grow and figures count up. With reduced motion everything is simply
 * there. The pieces (ExplainBlock) come from the server already checked (server: ai/explain.ts).
 */
import { createContext, Fragment, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { smoothPath } from '../lib/region';
import type { ExplainBlock, Explanation } from '../lib/types';
import { cx } from './ui';

const STEP_MS = 700;
const reduced = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const delay = (s: number) => ({ '--ct-delay': `${s.toFixed(2)}s` }) as CSSProperties;
/** Drawn already (shown again, or reduced motion): everything is simply there. */
const Still = createContext(false);

/** The answer, revealed one part at a time. `onDone` fires once the last part is in; `instant` skips the drawing. */
export function ExplainBoard({ ex, instant, onDone }: { ex: Explanation; instant?: boolean; onDone?: () => void }) {
  // Parts: the header, then each block, then where the figures came from.
  const parts = ex.blocks.length + 2;
  const [still] = useState(() => !!instant || reduced());
  const [shown, setShown] = useState(() => (still ? parts : 1));
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const board = useRef<HTMLDivElement>(null);
  const [pen, setPen] = useState<{ x: number; y: number; on: boolean }>({ x: 0, y: 0, on: false });
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    if (shown >= parts) {
      const t = setTimeout(() => done.current?.(), 400);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setShown((n) => n + 1), shown === 1 ? 900 : STEP_MS);
    return () => clearTimeout(t);
  }, [shown, parts]);

  // The pen moves to the part just drawn, and the card scrolls to keep it in view.
  useLayoutEffect(() => {
    if (still) return;
    const el = refs.current[shown - 1];
    if (!el || !board.current) return;
    setPen({ x: el.offsetLeft - 14, y: el.offsetTop + 6, on: shown < parts });
    if (shown > 1) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [shown, parts, still]);

  const part = (i: number, node: ReactNode) =>
    i < shown && (
      <div key={i} ref={(el) => void (refs.current[i] = el)} className={i ? 'mt-5' : ''}>
        {node}
      </div>
    );

  return (
    <Still.Provider value={still}>
    <div ref={board} className={cx('relative', still && 'ct-still')}>
      <span
        aria-hidden
        className="ct-pen pointer-events-none absolute top-0 left-0 z-10 size-2.5 rounded-full bg-marker shadow-[0_0_0_4px_rgb(212_162_12/0.18),0_2px_6px_rgb(212_162_12/0.5)]"
        style={{ transform: `translate(${pen.x}px, ${pen.y}px)`, opacity: pen.on ? 1 : 0 }}
      />
      {part(0, <Header ex={ex} />)}
      {ex.blocks.map((b, i) => part(i + 1, <Block b={b} />))}
      {part(
        parts - 1,
        ex.sources.length > 0 && (
          <p className="ct-fade text-[11px] leading-snug text-faint">
            <span className="font-medium text-muted">From</span> {ex.sources.join(' · ')}
          </p>
        ),
      )}
    </div>
    </Still.Provider>
  );
}

function Header({ ex }: { ex: Explanation }) {
  return (
    <div>
      {ex.kicker && (
        <div className="ct-fade flex items-center gap-1.5 text-[10.5px] font-semibold tracking-[0.1em] text-muted uppercase">
          <span className="size-1.5 rounded-full bg-marker" />
          {ex.kicker}
        </div>
      )}
      <h3 className="app-title relative mt-1.5 inline-block pb-1.5 text-[19px] leading-[1.25] text-ink">
        <span className="ct-rise" style={delay(0.05)}>
          {ex.title}
        </span>
        <svg aria-hidden viewBox="0 0 200 8" preserveAspectRatio="none" className="absolute -bottom-0.5 left-0 h-2 w-full overflow-visible">
          <path d="M2 5.5C40 2.5 92 6.8 140 3.6S190 4.4 198 3" pathLength={1} fill="none" stroke="var(--color-marker)" strokeWidth={2.6} strokeLinecap="round" className="ct-draw" style={{ ...delay(0.35), opacity: 0.85 }} />
        </svg>
      </h3>
      {ex.summary && (
        <p className="ct-rise mt-2 text-[13px] leading-relaxed text-ink-2" style={delay(0.45)}>
          {ex.summary}
        </p>
      )}
    </div>
  );
}

function Heading({ children }: { children?: string }) {
  if (!children) return null;
  return (
    <div className="ct-fade mb-2.5 flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.09em] text-muted uppercase">
      <span className="h-[2px] w-3 rounded-full bg-marker" />
      {children}
    </div>
  );
}

function Block({ b }: { b: ExplainBlock }) {
  return (
    <section>
      <Heading>{b.heading}</Heading>
      {b.kind === 'equation' && <Equation b={b} />}
      {b.kind === 'flow' && <Flow b={b} />}
      {b.kind === 'bars' && <Bars b={b} />}
      {b.kind === 'trend' && <Trend b={b} />}
      {b.kind === 'stats' && <Stats b={b} />}
      {b.kind === 'steps' && <Steps b={b} />}
      {b.kind === 'timeline' && <Timeline b={b} />}
      {b.kind === 'note' && <Note b={b} />}
    </section>
  );
}

type Of<K extends ExplainBlock['kind']> = Extract<ExplainBlock, { kind: K }>;

// Pastel boxes, as on a whiteboard: the term being explained in matcha, the rest in blue, peach and pink.
const TERM_TONES = ['border-info/20 bg-info-soft', 'border-[#f2c9a7] bg-[#fdf1e7]', 'border-ai/15 bg-ai-soft'];

function Equation({ b }: { b: Of<'equation'> }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-2.5">
      {b.terms.map((t, i) => (
        <Fragment key={i}>
          {t.op && (
            <span className="ct-fade text-[19px] leading-none font-light text-muted" style={delay(i * 0.16)}>
              {t.op}
            </span>
          )}
          <div className={cx('ct-pop rounded-[10px] border px-2.5 py-1.5', t.result ? 'border-brand-line bg-brand-soft' : TERM_TONES[i % TERM_TONES.length])} style={delay(i * 0.16)}>
            <div className="text-[11px] leading-tight text-ink-2">{t.label}</div>
            {t.value && (
              <div className={cx('mt-0.5 text-[15px] leading-tight font-semibold', t.result ? 'text-brand' : 'text-ink')}>
                <CountUp text={t.value} delay={i * 160 + 150} />
              </div>
            )}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

function Flow({ b }: { b: Of<'flow'> }) {
  return (
    <ol>
      {b.nodes.map((n, i) => (
        <li key={i}>
          {i > 0 && (
            <svg aria-hidden width="16" height="22" className="ml-[17px] block overflow-visible">
              <path d="M8 1v16" pathLength={1} stroke="var(--color-marker)" strokeWidth={1.7} strokeLinecap="round" fill="none" className="ct-draw" style={{ ...delay(i * 0.32 - 0.12), '--ct-dur': '0.3s' } as CSSProperties} />
              <path d="M4.2 13.2L8 18l3.8-4.8" pathLength={1} stroke="var(--color-marker)" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" fill="none" className="ct-draw" style={{ ...delay(i * 0.32 + 0.12), '--ct-dur': '0.2s' } as CSSProperties} />
            </svg>
          )}
          <div className="ct-pop flex items-start gap-2.5 rounded-[10px] border border-ink/[0.09] bg-white/85 px-3 py-2" style={delay(i * 0.32)}>
            <span className="num mt-px grid size-5 shrink-0 place-items-center rounded-full bg-ink text-[10.5px] font-semibold text-white">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] leading-snug font-medium text-ink">{n.label}</span>
                {n.value && (
                  <span className="shrink-0 text-[12.5px] font-semibold text-ink">
                    <CountUp text={n.value} delay={i * 320 + 150} />
                  </span>
                )}
              </div>
              {n.note && <div className="mt-0.5 text-[11.5px] leading-snug text-muted">{n.note}</div>}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function Bars({ b }: { b: Of<'bars'> }) {
  const max = Math.max(...b.items.map((r) => Math.abs(r.value)), 1e-9);
  const anyHighlight = b.items.some((r) => r.highlight);
  return (
    <div className="space-y-2.5">
      {b.items.map((r, i) => {
        const strong = r.highlight || !anyHighlight;
        return (
          <div key={i} className="ct-rise" style={delay(i * 0.1)}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-[12.5px]">
              <span className={cx('min-w-0 truncate', r.highlight ? 'font-semibold text-ink' : 'text-ink-2')}>{r.label}</span>
              <span className="shrink-0 font-medium text-ink">
                <CountUp text={r.display} delay={i * 100 + 100} />
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-ink/[0.06]">
              <div
                className="ct-grow h-full rounded-full"
                style={{
                  ...delay(i * 0.1 + 0.1),
                  width: `${Math.max(2, (Math.abs(r.value) / max) * 100)}%`,
                  background: r.value < 0 ? 'var(--color-bad)' : strong ? 'var(--color-brand)' : 'color-mix(in oklab, var(--color-series-2) 45%, white)',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Trend({ b }: { b: Of<'trend'> }) {
  const W = 360;
  const H = 128;
  const padX = 10;
  const padT = 30;
  const padB = 22;
  const vals = b.points.map((p) => p.value);
  let lo = Math.min(...vals);
  let hi = Math.max(...vals);
  const pad = (hi - lo) * 0.12 || Math.abs(hi) * 0.1 || 1;
  lo -= pad;
  hi += pad;
  const n = b.points.length;
  const x = (i: number) => padX + ((W - padX * 2) * i) / Math.max(1, n - 1);
  const y = (v: number) => padT + (H - padT - padB) * (1 - (v - lo) / (hi - lo));
  const pts = b.points.map((p, i) => ({ x: x(i), y: y(p.value) }));
  const line = smoothPath(pts);
  const h = b.highlight ?? n - 1;
  const hp = pts[h]!;
  const bubble = b.points[h]!.display;
  const bw = Math.max(44, bubble.length * 7 + 16);
  const bx = Math.min(Math.max(hp.x - bw / 2, 0), W - bw);
  const labelled = n <= 7 ? b.points.map((_, i) => i) : [...new Set([0, Math.floor((n - 1) / 2), n - 1, h])];
  const id = `ct-trend-${useId().replace(/[^\w-]/g, '')}`;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full overflow-visible" role="img" aria-label={b.points.map((p) => `${p.label} ${p.display}`).join(', ')}>
        <defs>
          <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--color-brand)" stopOpacity={0.16} />
            <stop offset="100%" stopColor="var(--color-brand)" stopOpacity={0} />
          </linearGradient>
        </defs>
        {[0.33, 0.66].map((f) => (
          <line key={f} x1={padX} x2={W - padX} y1={padT + (H - padT - padB) * f} y2={padT + (H - padT - padB) * f} stroke="rgb(27 26 24 / 0.07)" strokeDasharray="3 4" />
        ))}
        <line x1={padX} x2={W - padX} y1={H - padB} y2={H - padB} stroke="rgb(27 26 24 / 0.14)" />
        <path d={`${line}L${pts[n - 1]!.x},${H - padB}L${pts[0]!.x},${H - padB}Z`} fill={`url(#${id})`} className="ct-fade" style={delay(1)} />
        <path d={line} pathLength={1} fill="none" stroke="var(--color-brand)" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="ct-draw" style={{ '--ct-dur': '1.2s' } as CSSProperties} />
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={i === h ? 0 : 2.4} fill="white" stroke="var(--color-brand)" strokeWidth={1.4} className="ct-pop" style={delay((i / Math.max(1, n - 1)) * 1.1)} />
        ))}
        <circle cx={hp.x} cy={hp.y} r={4} fill="var(--color-marker)" className="ct-ring" />
        <circle cx={hp.x} cy={hp.y} r={4.2} fill="var(--color-marker)" stroke="white" strokeWidth={1.5} className="ct-pop" style={delay(1.1)} />
        <g className="ct-pop" style={delay(1.25)}>
          <rect x={bx} y={Math.max(0, hp.y - 30)} width={bw} height={20} rx={6} fill="var(--color-ink)" />
          <text x={bx + bw / 2} y={Math.max(0, hp.y - 30) + 13.5} textAnchor="middle" fontSize={11} fontWeight={600} fill="white" className="num">
            {bubble}
          </text>
        </g>
        {labelled.map((i) => (
          <text key={i} x={pts[i]!.x} y={H - 6} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fontSize={10} fill={i === h ? 'var(--color-ink)' : 'var(--color-muted)'} fontWeight={i === h ? 600 : 400} className="ct-fade" style={delay(0.6)}>
            {b.points[i]!.label}
          </text>
        ))}
      </svg>
      {b.caption && (
        <p className="ct-rise mt-1.5 flex items-start gap-1.5 text-[12px] leading-snug text-ink-2" style={delay(1.3)}>
          <span className="mt-[3px] h-[2px] w-3 shrink-0 rounded-full bg-marker" />
          {b.caption}
        </p>
      )}
    </div>
  );
}

function Stats({ b }: { b: Of<'stats'> }) {
  return (
    <div className={cx('grid gap-2', b.items.length === 1 ? 'grid-cols-1' : b.items.length === 2 ? 'grid-cols-2' : 'grid-cols-3')}>
      {b.items.map((s, i) => (
        <div key={i} className="ct-pop min-w-0 rounded-xl border border-ink/[0.08] bg-white/85 px-3 py-2.5" style={delay(i * 0.14)}>
          <div className="truncate text-[11px] text-muted">{s.label}</div>
          <div className="app-title mt-0.5 truncate text-[21px] leading-tight text-ink">
            <CountUp text={s.value} delay={i * 140 + 120} />
          </div>
          {s.change && (
            <div className={cx('mt-0.5 truncate text-[11.5px] font-medium', s.tone === 'good' ? 'text-good' : s.tone === 'bad' ? 'text-bad' : 'text-muted')}>
              {s.tone === 'good' ? '▲ ' : s.tone === 'bad' ? '▼ ' : ''}
              {s.change}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const IMPACT = { high: 'bg-brand text-white', medium: 'bg-info-soft text-info', low: 'bg-ink/[0.06] text-muted' };

function Steps({ b }: { b: Of<'steps'> }) {
  return (
    <ol className="space-y-3">
      {b.items.map((s, i) => (
        <li key={i} className="ct-rise flex gap-3" style={delay(i * 0.22)}>
          <span className="relative grid size-7 shrink-0 place-items-center">
            <svg aria-hidden viewBox="0 0 28 28" className="absolute inset-0 overflow-visible">
              <path
                d="M14.6 3.4C20.8 3.3 25 7.7 24.8 14.1 24.6 20.3 19.9 24.9 13.7 24.8 7.5 24.6 3.2 20 3.3 13.8 3.4 8.2 7.3 4 12.9 3.7"
                pathLength={1}
                fill="none"
                stroke="var(--color-marker)"
                strokeWidth={1.8}
                strokeLinecap="round"
                className="ct-draw"
                style={{ ...delay(i * 0.22 + 0.05), '--ct-dur': '0.5s' } as CSSProperties}
              />
            </svg>
            <span className="num text-[12px] font-semibold text-ink">{i + 1}</span>
          </span>
          <div className="min-w-0 pt-0.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] leading-snug font-semibold text-ink">
              {s.title}
              {s.impact && <span className={cx('rounded-full px-1.5 py-px text-[10px] font-semibold tracking-wide uppercase', IMPACT[s.impact])}>{s.impact} impact</span>}
            </div>
            {s.detail && <div className="mt-0.5 text-[12.5px] leading-snug text-muted">{s.detail}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Timeline({ b }: { b: Of<'timeline'> }) {
  const n = b.items.length;
  return (
    <div className="relative">
      <svg aria-hidden className="absolute top-[24px] h-[2px] overflow-visible" style={{ left: `${50 / n}%`, right: `${50 / n}%`, width: `${100 - 100 / n}%` }} viewBox="0 0 100 2" preserveAspectRatio="none">
        <path d="M0 1H100" pathLength={1} stroke="rgb(27 26 24 / 0.25)" strokeWidth={2} vectorEffect="non-scaling-stroke" className="ct-draw" style={{ '--ct-dur': '0.9s' } as CSSProperties} />
      </svg>
      <div className="grid" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
        {b.items.map((e, i) => (
          <div key={i} className="flex flex-col items-center px-1 text-center">
            <div className="num ct-fade h-4 text-[11.5px] leading-4 font-semibold text-ink" style={delay((i / n) * 0.9)}>
              {e.when}
            </div>
            <span className="ct-pop relative mt-[3px] size-2.5 rounded-full border-2 border-white bg-ink shadow-[0_0_0_1px_rgb(27_26_24/0.2)]" style={delay((i / n) * 0.9)} />
            <div className="ct-rise mt-1.5 text-[11.5px] leading-snug text-muted" style={delay((i / n) * 0.9 + 0.1)}>
              {e.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Note({ b }: { b: Of<'note'> }) {
  const caution = b.tone === 'caution';
  return (
    <div className={cx('ct-note relative rounded-[6px] px-3.5 pt-3.5 pb-3 text-[12.5px] leading-relaxed shadow-[0_8px_18px_-10px_rgb(27_26_24/0.45)]', caution ? 'bg-[#ffeedd] text-[#4d3200]' : 'bg-[#fff6c4] text-ink')}>
      <span aria-hidden className="absolute -top-2 left-1/2 h-4 w-14 -translate-x-1/2 rotate-[-3deg] rounded-[2px] bg-white/65 shadow-[0_1px_2px_rgb(27_26_24/0.12)]" />
      {caution && <span className="font-semibold">Worth checking: </span>}
      {b.text}
    </div>
  );
}

/** A figure that counts up to its value, written the way it was given ("$1.2M", "68.4%", "-$3,200"). */
export function CountUp({ text, delay: wait = 0, ms = 900 }: { text: string; delay?: number; ms?: number }) {
  const still = useContext(Still) || reduced();
  const m = /^(.*?)(\d[\d,]*(?:\.\d+)?)(.*)$/.exec(text);
  const [shown, setShown] = useState(() => (m && !still ? `${m[1]}${format(0, m[2]!)}${m[3]}` : text));
  useEffect(() => {
    if (!m || still) return setShown(text);
    const target = Number(m[2]!.replace(/,/g, ''));
    let raf = 0;
    let start = 0;
    const tick = (now: number) => {
      start ||= now;
      const k = Math.min(1, (now - start) / ms);
      setShown(k >= 1 ? text : `${m[1]}${format(target * (1 - (1 - k) ** 3), m[2]!)}${m[3]}`);
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    const t = setTimeout(() => (raf = requestAnimationFrame(tick)), wait);
    return () => (clearTimeout(t), cancelAnimationFrame(raf));
  }, [text]);
  return <span className="num tabular-nums">{shown}</span>;
}

/** `v` written like `like`: the same decimals, and thousands separators if it had them. */
function format(v: number, like: string) {
  const decimals = like.split('.')[1]?.length ?? 0;
  return like.includes(',') ? v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : v.toFixed(decimals);
}
